import { zodResolver } from '@hookform/resolvers/zod';
import { IdentityProviders, type SignInOutput, useSignIn } from 'korint-sdk/react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form } from '../../forms/form';
import { FormPasswordInput } from '../../forms/form-password-input';
import { FormTextInput } from '../../forms/form-text-input';
import {
  getLastLoginMethod,
  getLastLoginMethodHint,
  saveLastLoginMethod,
} from '../../../utils/last-login-method';
import { IdentityProvider } from './identity-provider';
import { LightbulbIcon } from 'lucide-react';
import { MfaSelectionForm } from './mfa-selection-form';
import { type Challenge, resolveSignInChallenge } from './resolveSignInChallenge';
import { SmsChallengeForm } from './sms-challenge-form';
import { TotpChallengeForm } from './totp-challenge-form';
import { Text } from '@folio/components/ui/text';

type SignInErrorCode =
  | 'userNotFound'
  | 'invalidCredentials'
  | 'userNotConfirmed'
  | 'googleAccountExists';

const SIGN_IN_ERROR_MAP = new Map<string, SignInErrorCode>([
  ['UserNotFoundException', 'userNotFound'],
  ['User does not exist', 'userNotFound'],
  ['NotAuthorizedException', 'invalidCredentials'],
  ['Incorrect username or password', 'invalidCredentials'],
  ['UserNotConfirmedException', 'userNotConfirmed'],
  ['User is not confirmed', 'userNotConfirmed'],
  [
    'PreSignUp failed with error An account with this email already exists. Please sign in with Google.',
    'googleAccountExists',
  ],
  ['An account with this email already exists. Please sign in with Google.', 'googleAccountExists'],
]);

const matchSignInErrorCode = (errorMessage: string, errorName?: string): SignInErrorCode | null => {
  const errorCode = errorName || '';
  if (errorCode && SIGN_IN_ERROR_MAP.has(errorCode)) {
    return SIGN_IN_ERROR_MAP.get(errorCode)!;
  }
  for (const [pattern, code] of SIGN_IN_ERROR_MAP.entries()) {
    if (errorMessage.includes(pattern)) {
      return code;
    }
  }
  return null;
};

export const SignInForm = ({
  onSuccess,
  providers = [],
  showLastLoginHint = false,
  defaultEmail,
}: {
  onSuccess: (res: SignInOutput | undefined, email: string) => void;
  providers?: IdentityProviders[];
  showLastLoginHint?: boolean;
  defaultEmail?: string;
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [lastLoginMethodHint, setLastLoginMethodHint] = useState<string | null>(null);
  const [lastLoginMethod, setLastLoginMethod] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);

  useEffect(() => {
    if (!showLastLoginHint) return;

    const method = getLastLoginMethod();
    setLastLoginMethod(method);
    setLastLoginMethodHint(getLastLoginMethodHint(method, t));
  }, [showLastLoginHint, t]);

  const { signIn, isLoading: signInLoading } = useSignIn({
    onSuccess: (result: SignInOutput | undefined) => {
      if (result?.isSignedIn) {
        if (showLastLoginHint) {
          saveLastLoginMethod('email');
        }
        onSuccess(result, form.getValues().email);
        return;
      }
      const { email, password } = form.getValues();
      const next = result ? resolveSignInChallenge(result.nextStep, email) : null;
      if (next) {
        setCredentials({ email, password });
        setChallenge(next);
        return;
      }
      setError(t('authSignIn.errors.generic'));
    },
    onError: (err: any) => {
      const code = matchSignInErrorCode(err.message, err.name);
      setError(code ? t(`authSignIn.errors.${code}`) : t('authSignIn.errors.generic'));
    },
  });
  const resolverSchema = useMemo(
    () =>
      z.object({
        email: z.string().trim().email(t('authSignIn.validation.emailRequired')),
        password: z.string().min(1, t('authSignIn.validation.passwordRequired')),
      }),
    [t],
  );
  const form = useForm<{
    email: string;
    password: string;
  }>({
    defaultValues: {
      email: defaultEmail ?? '',
      password: '',
    },
    resolver: zodResolver(resolverSchema),
  });

  const handleSubmit = form.handleSubmit(async ({ email, password }) => {
    setError(null);
    await signIn({
      username: email,
      password,
    });
  });

  if (challenge?.step === 'totp-code') {
    return (
      <TotpChallengeForm
        email={challenge.email}
        onSuccess={() => onSuccess(undefined, challenge.email)}
      />
    );
  }
  if (challenge?.step === 'sms-code') {
    return (
      <SmsChallengeForm
        email={challenge.email}
        password={credentials?.password ?? ''}
        destination={challenge.destination}
        onSuccess={() => onSuccess(undefined, challenge.email)}
      />
    );
  }
  if (challenge?.step === 'mfa-selection') {
    return (
      <MfaSelectionForm
        email={challenge.email}
        allowedMfaTypes={challenge.allowedMfaTypes}
        onChallenge={setChallenge}
        onSuccess={() => onSuccess(undefined, challenge.email)}
      />
    );
  }

  return (
    <Form {...form}>
      {lastLoginMethodHint && (
        <Alert className="mb-4" variant="info">
          <LightbulbIcon />
          <Text inline size="sm">
            {lastLoginMethodHint}
          </Text>
        </Alert>
      )}

      <IdentityProvider
        providers={providers}
        context="signin"
        lastUsedProvider={lastLoginMethod}
        enableLastLoginTracking={showLastLoginHint}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <Alert className="mb-4" variant="destructive">
            {error}
          </Alert>
        )}
        <FormTextInput
          name="email"
          type="email"
          label={t('authSignIn.emailLabel')}
          placeholder={t('authSignIn.emailPlaceholder')}
          required
          control={form.control}
          disabled={!!defaultEmail}
        />
        <FormPasswordInput
          name="password"
          label={t('authSignIn.passwordLabel')}
          placeholder={t('authSignIn.passwordPlaceholder')}
          required
          control={form.control}
        />
        <Button className="w-full" type="submit" loading={signInLoading}>
          {t('authSignIn.signInButton')}
        </Button>
        <ForgotPasswordLink email={defaultEmail} />
      </form>
    </Form>
  );
};

const ForgotPasswordLink = ({ email }: { email?: string }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  if (!email) return null;

  const search = new URLSearchParams(searchParams);
  search.set('email', email);

  return (
    <Text size="sm" className="text-center">
      <Link
        to={{ pathname: '/auth/setup-password', search: search.toString() }}
        className="font-semibold text-primary hover:text-primary/90"
      >
        {t('authSignIn.forgotPassword')}
      </Link>
    </Text>
  );
};
